import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { RoleUpdateStore } from "../../store";
import { InputString } from "../../../common/controls";
import { InputReferenceSet } from "../../../common/controls";
import { CreateEntityDialog } from "../../../common/controls";
import { PermissionCreate } from "../../../permission/components/create/PermissionCreate";
import { PermissionCreateStore } from "../../../permission/store";
import { InputReference } from "../../../common/controls";
import { InputBoolean } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type RoleUpdateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  name_es: string;
  name_esSelectedValue?: { displayId: string; id: string } | null;
  onName_esChange?: (value: string) => void;
  name_esError?: string;
  name_en: string;
  name_enSelectedValue?: { displayId: string; id: string } | null;
  onName_enChange?: (value: string) => void;
  name_enError?: string;
  name_pt: string;
  name_ptSelectedValue?: { displayId: string; id: string } | null;
  onName_ptChange?: (value: string) => void;
  name_ptError?: string;
  permissionsSelectedValues: { displayId: string; id: string }[];
  onPermissionsChange?: (value: string) => void;
  onPermissionsSelected?: (value: { displayId: string; id: string }[]) => void;
  permissionsOptions?: { displayId: string; id: string }[];
  permissionsError?: string;
  audience: string;
  audienceSelectedValue?: { displayId: string; id: string } | null;
  onAudienceChange?: (value: string) => void;
  audienceError?: string;
  isForAllTenants: boolean;
  isForAllTenantsSelectedValue?: { displayId: string; id: string } | null;
  onIsForAllTenantsChange?: (value: boolean) => void;
  isForAllTenantsError?: string;
  roleUpdateStore: RoleUpdateStore;
};

export const RoleUpdateForm: React.FC<RoleUpdateFormProps> = observer(
  ({
    onSave = () => {},
    onCancel = () => {},
    saving = false,
    showToolbox = true,
    showSaveFooter,
    name_es = "",
    name_esError,
    onName_esChange = () => {},
    name_en = "",
    name_enError,
    onName_enChange = () => {},
    name_pt = "",
    name_ptError,
    onName_ptChange = () => {},
    onPermissionsSelected = () => {},
    permissionsOptions = [],
    permissionsSelectedValues,
    permissionsError,
    onPermissionsChange = () => {},
    audience = "",
    audienceError,
    onAudienceChange = () => {},
    isForAllTenants = false,
    isForAllTenantsError,
    onIsForAllTenantsChange = () => {},
    roleUpdateStore,
  }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    const audienceIsVisible = ["SysAdmin"].some((role) =>
      rootStore.authStore.hasRole(role)
    );
    return (
      <Grid>
        {showToolbox && (
          <FormToolbox
            saveAndCloseLabel={langStore.saveEntity}
            closeLabel={langStore.cancelUpdatedEntity}
            saveForm={() => onSave()}
            saveDisabled={saving}
            closeForm={() => onCancel()}
          />
        )}
        <Box>
          <FormExtras
            entity="Role"
            mode="update"
            values={{}}
            options={{}}
            onPatch={(campo: string, valor: any) => {
              switch (campo) {
                case "name_es":
                  onName_esChange?.(valor);
                  break;
                case "name_en":
                  onName_enChange?.(valor);
                  break;
                case "name_pt":
                  onName_ptChange?.(valor);
                  break;
                case "audience":
                  onAudienceChange?.(valor);
                  break;
                case "isForAllTenants":
                  onIsForAllTenantsChange?.(valor);
                  break;
              }
            }}
          />
          <GroupContainer
            id="null"
            errorText={roleUpdateStore.getCardErrors("null")}
            title={langStore.groupRoleLabel}
            collapsible={false}
          >
            <FormControl fullWidth>
              <InputString
                autoFocus
                value={name_es}
                error={Boolean(name_esError)}
                warning={
                  !name_esError && Boolean(roleUpdateStore.warnings["name_es"])
                }
                helperText={
                  name_esError || roleUpdateStore.warnings["name_es"]?.message
                }
                label={langStore.RoleNameLabel + " (es)"}
                onChange={(value: string) => onName_esChange(value)}
                description={langStore.RoleNameDescription}
              />
              <InputString
                value={name_en}
                error={Boolean(name_enError)}
                warning={
                  !name_enError && Boolean(roleUpdateStore.warnings["name_en"])
                }
                helperText={
                  name_enError || roleUpdateStore.warnings["name_en"]?.message
                }
                label={langStore.RoleNameLabel + " (en)"}
                onChange={(value: string) => onName_enChange(value)}
                description={langStore.RoleNameDescription}
              />
              <InputString
                value={name_pt}
                error={Boolean(name_ptError)}
                warning={
                  !name_ptError && Boolean(roleUpdateStore.warnings["name_pt"])
                }
                helperText={
                  name_ptError || roleUpdateStore.warnings["name_pt"]?.message
                }
                label={langStore.RoleNameLabel + " (pt)"}
                onChange={(value: string) => onName_ptChange(value)}
                description={langStore.RoleNameDescription}
              />
              <InputReferenceSet
                id="permissions"
                label={langStore.RolePermissionsLabel}
                error={Boolean(permissionsError)}
                helperText={
                  permissionsError ||
                  roleUpdateStore.warnings["permissions"]?.message
                }
                selectedValues={permissionsSelectedValues}
                onChange={(value: string) => onPermissionsChange(value)}
                onSelect={(value: { displayId: string; id: string }[]) =>
                  onPermissionsSelected(value)
                }
                options={permissionsOptions}
                description={langStore.RolePermissionsDescription}
                nullText={langStore.optionalFieldHint}
                canCreate={rootStore.authStore.hasPermissions(
                  "permission_create"
                )}
                renderCreateDialog={(dialogProps) => (
                  <CreateEntityDialog
                    {...dialogProps}
                    createStore={(rootStore) =>
                      new PermissionCreateStore(rootStore)
                    }
                    renderForm={(store) => (
                      <PermissionCreate
                        permissionCreateStore={store}
                        showToolbox={false}
                        showSaveFooter={false}
                      />
                    )}
                    saveMethod="saveNewPermission"
                    cancelMethod="cancelNewPermission"
                  />
                )}
              />
              {audienceIsVisible && (
                <InputReference
                  id="audience"
                  error={Boolean(audienceError)}
                  helperText={
                    audienceError ||
                    roleUpdateStore.warnings["audience"]?.message
                  }
                  label={langStore.RoleAudienceLabel}
                  options={langStore.audienceOptions}
                  selectedValue={
                    audience
                      ? {
                          id: audience,
                          displayId:
                            langStore.audienceOptions.find(
                              (o: { id: string }) => o.id === audience
                            )?.displayId || audience,
                        }
                      : null
                  }
                  onChange={() => {}}
                  disabled={true}
                  onSelect={() => {}}
                />
              )}
              <InputBoolean
                value={isForAllTenants}
                error={Boolean(isForAllTenantsError)}
                helperText={
                  isForAllTenantsError ||
                  roleUpdateStore.warnings["isForAllTenants"]?.message
                }
                label={langStore.RoleIsForAllTenantsLabel}
                onChange={(value: boolean) => onIsForAllTenantsChange(value)}
              />
            </FormControl>
          </GroupContainer>
        </Box>
        {(showSaveFooter ?? !showToolbox) && (
          <FormSaveFooter
            onSave={() => onSave()}
            label={langStore.saveEntity}
            disabled={saving}
          />
        )}
      </Grid>
    );
  }
);
